import * as React from "react";
import { ThumbsDown, ThumbsUp, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { postFeedback } from "@/lib/api";

// A server message id is a UUID; optimistic client bubbles use `assistant-<ts>`
// / `user-<ts>` / `ddq-user-<ts>`. The feedback endpoint 404s on non-server ids,
// so callers gate the widget on this.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isServerMessageId(id: string | undefined | null): boolean {
  return typeof id === "string" && UUID_RE.test(id);
}

/**
 * WS7 flywheel — per-answer feedback widget.
 *
 * Thumbs up/down on an AI answer → POST /v1/feedback. A down-vote opens an
 * optional correction note (what the right answer / source should have been);
 * the note is recorded with the rating so a gp_team reviewer can later promote
 * it to a golden candidate in the admin FeedbackTab.
 *
 * `messageId` is the assistant message id. For DDQ answers (which share one
 * message id across the whole response) `context` names the specific question
 * so the correction note is self-describing in the admin tab.
 *
 * Calm chrome by GreenPoint standards: ghost icon buttons that turn primary
 * (up) / warning (down) once chosen; a single line confirmation after submit.
 */
export function FeedbackWidget({
  messageId,
  context,
  className,
}: {
  messageId: string;
  /** Optional question context (DDQ) prepended to the correction note. */
  context?: string;
  className?: string;
}) {
  const [rating, setRating] = React.useState<"up" | "down" | null>(null);
  const [showNote, setShowNote] = React.useState(false);
  const [note, setNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // A message id is required to attach feedback. Pending/optimistic bubbles
  // (no id yet) simply don't render the widget.
  if (!messageId) return null;

  const send = async (value: "up" | "down", correction?: string) => {
    setSubmitting(true);
    setError(null);
    try {
      const composed = correction?.trim()
        ? context
          ? `[${context}] ${correction.trim()}`
          : correction.trim()
        : undefined;
      await postFeedback(messageId, value, composed);
      setRating(value);
      setDone(true);
      setShowNote(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not record feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className={cn("flex items-center gap-1.5 text-xs text-muted-foreground", className)}>
        <Check className="size-3.5 text-primary" />
        Thanks — your feedback was recorded.
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground">Was this answer helpful?</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Helpful"
          aria-pressed={rating === "up"}
          disabled={submitting}
          className={cn("size-7", rating === "up" && "text-primary")}
          onClick={() => void send("up")}
        >
          {submitting && rating !== "down" ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <ThumbsUp className="size-3.5" />
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Not helpful"
          aria-pressed={showNote}
          disabled={submitting}
          className={cn("size-7", showNote && "text-warning")}
          onClick={() => setShowNote((v) => !v)}
        >
          <ThumbsDown className="size-3.5" />
        </Button>
      </div>

      {showNote ? (
        <div className="space-y-2">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional: what should the answer (or its sources) have said?"
            className="min-h-16 text-sm"
            aria-label="Correction note"
            onKeyDown={(e) => {
              // Enter submits, Shift+Enter newlines — matches the chat input.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send("down", note);
              }
            }}
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="subtle"
              size="sm"
              disabled={submitting}
              onClick={() => void send("down", note)}
            >
              {submitting ? <Loader2 className="size-3.5 animate-spin" /> : null}
              Submit feedback
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={submitting}
              onClick={() => {
                setShowNote(false);
                setNote("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {error ? <div className="text-xs text-destructive">{error}</div> : null}
    </div>
  );
}
